import { useCallback, useState } from 'react'

import { useErrorSnackbar } from 'shared/hooks'
import { SingleSelectRow } from '../types'

interface UseSingleSelectActionsArgs<T extends SingleSelectRow> {
  rows: T[]
  refetch: () => Promise<unknown>
  onAddAction: (itemId: string) => Promise<unknown>
  onRemoveAction: (itemId: string) => Promise<unknown>
}

export const useSingleSelectActions = <T extends SingleSelectRow>({
  rows,
  refetch,
  onAddAction,
  onRemoveAction,
}: UseSingleSelectActionsArgs<T>) => {
  const [showError] = useErrorSnackbar()

  const [itemToRemove, setActionToRemove] = useState<T | null>(null)

  const [isAddActionLoading, setIsAddActionLoading] = useState(false)

  const [isRemoveActionLoading, setIsRemoveActionLoading] = useState(false)

  const requestRemoveAction = useCallback(
    (itemId: string) => {
      const item = rows.find(row => row.id === itemId)

      if (!item) return

      setActionToRemove(item)
    },
    [rows]
  )

  const confirmRemoveAction = useCallback(async () => {
    if (!itemToRemove || isRemoveActionLoading) {
      return
    }

    setIsRemoveActionLoading(true)

    try {
      await onRemoveAction(itemToRemove.id)
      await refetch()
      setActionToRemove(null)
    } catch {
      showError()
    } finally {
      setIsRemoveActionLoading(false)
    }
  }, [itemToRemove, isRemoveActionLoading, onRemoveAction, refetch, showError])

  const addAction = useCallback(
    async (itemId: string) => {
      if (isAddActionLoading) return

      setIsAddActionLoading(true)

      try {
        await onAddAction(itemId)
        await refetch()
      } finally {
        setIsAddActionLoading(false)
      }
    },
    [isAddActionLoading, onAddAction, refetch]
  )

  return {
    itemToRemove,
    isAddActionLoading,
    isRemoveActionLoading,
    cancelRemoveAction: () => setActionToRemove(null),
    requestRemoveAction,
    confirmRemoveAction,
    addAction,
  }
}
